/**
 * Pure converter: AILayout template artifact → trace scene node specs.
 *
 * Emits a parent-first flat list of SceneObjectData (Layout/LayoutItem/Text
 * behaviors attached) plus the list of AI-fillable slots. No Babylon imports —
 * materialization against a live scene lives in the place_template tool.
 *
 * Unit domains (see types.ts): artifact lengths are du (1 du = 1 mm) → meters
 * ×0.001; artifact fontSize is a du em-square → trace centi-world-unit
 * fontSize, du/10.
 *
 * Both "known simplifications" this header used to list are gone and the note
 * outlived them: container surface styling materializes (`cardBodyData` — a
 * slab, `Bevel` from `radius`, `Renderer` from `color`/`frame`), and
 * `alignCrossB` is mapped. What is still unmapped lives in CONTRACT.md §4,
 * which the coverage gate enforces — a second list here only goes stale.
 */

import type { SceneObjectData } from 'trace-viewer/scene';
import type { LayoutData, LayoutItemData, LayoutPaddingData, BevelData, MorphStretchData, RendererData, TextData } from 'trace-viewer/behaviors';

import type {
    FreedomValue, TemplateArtifact, TemplateNode, TemplateNodeProps, TemplateSlot,
} from './types';
import { templateBehaviors } from './behaviors';
// The measured kit inventory, imported as data rather than through wrappers.ts:
// convert.ts is what wrappers.ts's consumers call, and a cycle here is a lint error.
import componentMetricsJson from './wrappers/component-metrics.json';

const COMPONENT_METRICS = componentMetricsJson as unknown as Record<string, unknown>;

export const DU_TO_M = 0.001;

/**
 * Depth ladder for containers whose artifact depth is flat (the 2D artifacts
 * author z: 1 du — a DOM-canvas convention with no z stacking). The 3D engine
 * front-flushes flowed children (child z = containerDepth − childDepth), so a
 * flat container gives its content no z clearance over the 6 mm body slabs
 * (Absolute items, centered at the container origin, face at +3 mm) — slot
 * fills rendered BEHIND their card and vanished (showcase-v2 z-bury).
 *
 * Decreasing depth per nesting level advances each level's content 8 mm in
 * front of its parent's origin: every body face clears its ancestor's by
 * 2 mm and leaf content clears every ancestor face.
 */
const CONTAINER_DEPTH_ROOT = 0.032;
const CONTAINER_DEPTH_STEP = 0.008;
const CONTAINER_DEPTH_MIN = 0.008;
/**
 * Artifact depths below this are "flat" 2D-convention values, not intent.
 *
 * Exported with {@link DU_TO_M} because the coverage ledger has to answer the
 * same question — is this container's depth real? — to report `alignCrossB` as
 * consumed or dropped (CONTRACT.md §6.1). Re-declaring the number there would
 * let the two drift apart silently.
 */
export const FLAT_DEPTH_THRESHOLD = 0.008;

function ladderDepth(level: number): number {
    return Math.max(CONTAINER_DEPTH_ROOT - CONTAINER_DEPTH_STEP * level, CONTAINER_DEPTH_MIN);
}

/**
 * Role colors applied to template surfaces at placement. The model derives a
 * palette from the design intent; unset roles fall back to a warm neutral set.
 *
 * These are **theme values, not baked paint**: the converter emits a binding to
 * the corresponding theme property (`{backgroundColor}`, `{panelColor}`, …) and
 * the placed root declares them, so swapping the style afterwards re-resolves
 * every surface in one commit instead of re-walking the scene to repaint it.
 * See THEME_BINDINGS and trace-viewer `docs/STYLES.md`.
 */
export interface TemplatePalette {
    /** Whole-canvas backdrop (the template root surface). */
    background?: string;
    /** Card/panel bodies (framed containers). */
    surface?: string;
    /** Highlight color — reserved for slot fills, not applied by the converter. */
    accent?: string;
    /** Default text color for template-authored text without an explicit color. */
    text?: string;
}

export const DEFAULT_PALETTE: Required<TemplatePalette> = {
    background: '#EFEDE8',
    surface: '#F8F7F4',
    accent: '#C96F4A',
    text: '#2A2724',
};

export interface ConvertedNode {
    /** Index into the emitted list of this node's parent; -1 for the root. */
    parentIndex: number;
    data: SceneObjectData;
}

export interface ConvertedTemplate {
    /** Parent-first: a node's parent always precedes it. */
    nodes: ConvertedNode[];
    slots: TemplateSlot[];
    /**
     * Template-level guidance authored on nodes that produce no slot — every
     * site in the library is the root ("No transparent background — give this
     * card a solid background color…"). Belongs in the tool result, never in
     * the scene. See CONTRACT.md §3.5.
     */
    guidance: string[];
}

/**
 * Does this tree carry any renderable asset that is not a doodad?
 *
 * Answers "is it safe to suppress doodads here" — see the dropDoodads note in
 * convertTemplate. Slots and containers do not count: a wrapper whose only
 * geometry is doodads has nothing left once they go.
 */
function hasNonDoodadGeometry(node: TemplateNode): boolean {
    if (node.type === 'asset' && node.properties?.asset
        && node.properties.componentType !== 'doodad') return true;
    return (node.children ?? []).some(hasNonDoodadGeometry);
}

/** "slot:col-1" → "col-1"; bare names pass through. */
function displayName(node: TemplateNode): string {
    const raw = node.name ?? node.id ?? node.type;
    const sep = raw.indexOf(':');
    return sep >= 0 ? raw.slice(sep + 1) : raw;
}

function isOpen<T>(v: FreedomValue<T> | undefined):
    v is { freedom: 'open'; hint?: string; suggestion?: string } {
    return typeof v === 'object' && v !== null && (v as { freedom?: string }).freedom === 'open';
}

/** Resolve a freedom-wrapped value to a concrete one, or undefined if open. */
function resolveValue<T>(v: FreedomValue<T> | undefined): T | undefined {
    if (v === undefined || isOpen(v)) return undefined;
    if (typeof v === 'object' && v !== null && 'freedom' in (v as object)) {
        const w = v as { freedom: string; value?: T; options?: T[]; default?: T };
        if (w.freedom === 'locked') return w.value;
        if (w.freedom === 'menu') return w.default ?? w.options?.[0];
        return undefined;
    }
    return v as T;
}

function openHint<T>(v: FreedomValue<T> | undefined): string | undefined {
    return isOpen(v) ? (v.hint ?? v.suggestion) : undefined;
}

const DIRECTION: Record<string, LayoutData['direction']> = {
    horizontal: 'X', vertical: 'Y', depth: 'Z',
};
const JUSTIFY: Record<string, LayoutData['alignMain']> = {
    start: 'Start', center: 'Center', end: 'End',
};
/**
 * `node.align` is the AI vocabulary for the CONTAINER's cross-axis alignment —
 * align-items, not per-item align-self (prototype `DirectConverter.ts:392`,
 * `alignCrossA = archAlignA ?? resolveCrossAlignment(sNode.align) ?? 'Start'`).
 * The engine field `alignCrossA` outranks it: saved templates write the engine
 * field, AI-generated nodes write `align`. See CONTRACT.md §3.1.
 */
const CROSS_ALIGN: Record<string, LayoutData['alignCross']> = {
    start: 'Start', center: 'Center', end: 'End', stretch: 'Stretch',
};

/**
 * How a model seats in depth relative to the surface behind it
 * (`semanticProps.modelAnchor`). The prototype resolves this in SceneBinder,
 * against the ancestor's front plane: `behind` puts the model's FRONT face
 * there and it extends backward, `centered` puts its CENTRE there, `forward`
 * puts its BACK face there so it pops toward the viewer clear of the surface.
 *
 * For a slot the converter emits, that plane is the slot's own depth box, so
 * the rule is the container's cross-B alignment. It is NOT the same statement
 * as the authored `alignCrossB` and outranks it on a model3d node — the
 * prototype's anchoring overrides the layout's z for exactly these nodes.
 * See CONTRACT.md §3.3.
 */
const MODEL_ANCHOR_CROSS_B: Record<string, LayoutData['alignCrossB']> = {
    behind: 'Start', centered: 'Center', forward: 'End',
};

function modelAnchor(node: TemplateNode): 'behind' | 'centered' | 'forward' | undefined {
    const a = (node as { semanticProps?: { modelAnchor?: string } }).semanticProps?.modelAnchor;
    return a === 'behind' || a === 'centered' || a === 'forward' ? a : undefined;
}

type Axis = 'width' | 'height' | 'depth';
const AXES: [keyof NonNullable<TemplateNodeProps['size']>, Axis, 'widthMode' | 'heightMode' | 'depthMode'][] = [
    ['x', 'width', 'widthMode'],
    ['y', 'height', 'heightMode'],
    ['z', 'depth', 'depthMode'],
];

/** Item sizing shared by containers and leaves. */
function itemFields(props: TemplateNodeProps, node?: TemplateNode): Partial<Omit<LayoutItemData, '@type'>> {
    const out: Partial<Omit<LayoutItemData, '@type'>> = {};
    // Out-of-flow nodes (overlay backdrops, breakout subjects) keep their
    // authored position — flowing them shrink-crushes the whole composition.
    if (node?.position === 'absolute' || props.absolute) out.positionMode = 'Absolute';
    if (props.size) {
        for (const [du, sizeField, modeField] of AXES) {
            const v = props.size[du];
            if (v === 'hug') out[modeField] = 'Hug';
            else if (v === 'fill') out[modeField] = 'Fill';
            else if (typeof v === 'number') {
                out[sizeField] = v * DU_TO_M;
                out[modeField] = 'Fixed';
            }
        }
    }
    if (props.flexGrow !== undefined) out.flexGrow = props.flexGrow;
    return out;
}

function layoutBehavior(props: TemplateNodeProps, node?: TemplateNode): LayoutData {
    const layout: LayoutData = {
        '@type': 'Layout',
        direction: DIRECTION[props.direction ?? 'vertical'],
        alignMain: JUSTIFY[props.justify ?? 'start'],
        alignCross: props.alignCrossA ?? CROSS_ALIGN[node?.align?.toLowerCase() ?? ''] ?? 'Start',
        // The trace defaults are 0.02 m; artifact semantics are "unset = 0".
        gap: (props.gap ?? 0) * DU_TO_M,
        padding: 0,
        ...itemFields(props, node),
    };

    // Fill children need a definite parent extent. The prototype resolves
    // fill-in-hug through the ancestor chain; our engine falls back to the
    // 1m default and rows explode. Promote the container to Fill so it takes
    // its extent from ITS parent (the fixed-width template root).
    const axisKey = ({ horizontal: 'x', vertical: 'y', depth: 'z' } as const)[props.direction ?? 'vertical'];
    const modeField = ({ x: 'widthMode', y: 'heightMode', z: 'depthMode' } as const)[axisKey];
    const hasFillChild = node?.children?.some(c => c.properties?.size?.[axisKey] === 'fill');
    if (hasFillChild && (layout[modeField] === 'Hug' || layout[modeField] === undefined)) {
        layout[modeField] = 'Fill';
    }
    if (typeof props.padding === 'number') {
        layout.padding = props.padding * DU_TO_M;
    } else if (props.padding) {
        const p: LayoutPaddingData = {};
        if (props.padding.left) p.left = props.padding.left * DU_TO_M;
        if (props.padding.right) p.right = props.padding.right * DU_TO_M;
        if (props.padding.top) p.top = props.padding.top * DU_TO_M;
        if (props.padding.bottom) p.bottom = props.padding.bottom * DU_TO_M;
        layout.padding = p;
    }
    return layout;
}

/** Built-in cube primitive src (see trace-viewer BuiltInPrimitives). */
const CUBE_SRC = 'builtin:primitive_cube.glb';

/**
 * Design-set component id → served GLB path. Component ids spell the type
 * out ("Background"); the GLB files use the manifest's short tokens ("BG").
 * The `{set}` placeholder is substituted at conversion time.
 */
const COMPONENT_FILE_TOKENS: Record<string, string> = { Background: 'BG' };

/** Minimum fraction of rest size for axes that cannot shrink via morphs. */
const NO_SHRINK_FLOOR = 0.7;

export interface ComponentSizeMetrics {
    rest: [number, number, number];
    morphs: Record<'X' | 'Y' | 'Z', { in: boolean; out: boolean }>;
}

/**
 * Clamp a requested component box to what the asset can achieve: axes
 * without In morphs saturate near 0.7×rest (the prototype's proxy pipeline
 * behaves this way — overlapping fins are its slat-wall look, and
 * leaf-squeezing further compresses the sculpted texture).
 */
export function clampComponentSize(
    requested: { width?: number; height?: number; depth?: number },
    metrics: ComponentSizeMetrics | undefined,
): { width?: number; height?: number; depth?: number } {
    if (!metrics) return requested;
    const axes = [['width', 0, 'X'], ['height', 1, 'Y'], ['depth', 2, 'Z']] as const;
    const out = { ...requested };
    for (const [key, i, axis] of axes) {
        const v = out[key];
        const rest = metrics.rest[i];
        if (typeof v !== 'number' || !(rest > 0)) continue;
        if (v < rest && !metrics.morphs[axis].in) {
            out[key] = Math.max(v, rest * NO_SHRINK_FLOOR);
        }
    }
    return out;
}

/**
 * Where a component goes when the requested set does not ship it. The
 * prototype's choice; an aesthetic decision, so it stays named rather than
 * being inferred from whatever the inventory lists first.
 */
const FALLBACK_SET = 'FacetedCorner';

/** `SM_{set}_Base_01_A` → the filename the kit would ship for it. */
function componentFilename(assetId: string, set: string): string {
    let file = assetId.replaceAll('{set}', set);
    for (const [word, token] of Object.entries(COMPONENT_FILE_TOKENS)) {
        file = file.replace(`_${word}_`, `_${token}_`);
    }
    return `${file}.glb`;
}

/**
 * Resolve a component id against a design set, asking the kit rather than the
 * name.
 *
 * Two name branches used to live here: `Doodad` + `Core` fell back to
 * `FacetedCorner`, and a template's own catalog piece resolved to a bare
 * filename when the set was Core. Both encoded "does this set ship this
 * component?" as a guess about spelling. `COMPONENT_METRICS` answers it as a
 * fact — it is the measured inventory, 256 entries across 32 sets plus the 13
 * bare filenames that ARE the Core geometry (the kit ships no `SM_Core_*`).
 *
 * Order of preference: the requested set; then the bare Core filename; then a
 * set that does ship it, so a doodad still renders rather than resolving to a
 * path nothing can serve. See CONTRACT.md §5.2.
 */
export function componentAssetPath(assetId: string, set: string): string {
    const requested = componentFilename(assetId, set);
    if (COMPONENT_METRICS[requested]) return `/components/spatial/${requested}`;

    // Core: the generic filename is the component, so `SM_{set}_Base_01_A`
    // becomes `base.glb`. Derived from the id's own role word, not a table.
    const role = /_([A-Za-z]+)_\d+_[A-Z]$/.exec(assetId.replaceAll('{set}', 'X'))?.[1];
    if (role) {
        const bare = [`${role.toLowerCase()}.glb`, `${role}.glb`]
            .find(f => COMPONENT_METRICS[f]);
        if (bare) return `/components/spatial/${bare}`;
    }

    // Nothing bare either — the Core set ships no doodad, for instance. Fall
    // back to a NAMED set rather than whichever one happens to sort first: the
    // prototype picks FacetedCorner here, and that is an aesthetic choice, not
    // a fact the inventory can answer. What the inventory does answer is
    // whether the fallback ships the piece, which is the part that used to be
    // a guess keyed on the substring "Doodad".
    const fallback = componentFilename(assetId, FALLBACK_SET);
    if (COMPONENT_METRICS[fallback]) return `/components/spatial/${fallback}`;

    // Last resort: any set that ships it, so the piece renders rather than
    // resolving to a path nothing can serve. The suffix comes from the id
    // itself — `SM_{set}_Doodad_01_A` → `_Doodad_01_A.glb`.
    const suffix = componentFilename(assetId, '\u0000').split('\u0000')[1];
    const shipped = suffix && Object.keys(COMPONENT_METRICS).find(f => f.endsWith(suffix));
    return `/components/spatial/${shipped ?? requested}`;
}


/**
 * A `semanticProps.backgroundColor` is a real surface colour the converter was
 * dropping: iphone-screen's `slot:screen` is a `page` node authored `#000000`,
 * and the screen rendered unpainted. Fold it into the node's own `color` so it
 * takes the ordinary card-body path — it is template-authored, so it stays a
 * literal rather than binding to the theme (CONTRACT.md §3.4). An explicit
 * `properties.color` is the more specific statement and wins.
 */
function surfaceColorNode(node: TemplateNode): TemplateNode | null {
    const bg = (node as { semanticProps?: { backgroundColor?: string } }).semanticProps?.backgroundColor;
    if (typeof bg !== 'string' || !bg) return null;
    if (node.properties?.color !== undefined) return null;
    return { ...node, properties: { ...node.properties, color: bg } };
}

/**
 * A wrapper set piece's authored depth, in metres. {#zoffset}
 *
 * ONE convention, not two. `zOffset` is written by our own extractor
 * (`scripts/extract-prototype-wrappers.mjs`): the mount is placed in the front
 * region at `depth/2 - mz/2`, and every other layer is laid from the back wall
 * with a cursor starting at `-depth/2`. Both are the same signed offset from
 * the container centre, and in both **positive means toward the front face**.
 * The nested-positive / sibling-negative split this used to branch on was never
 * two conventions — it is just where each family sits in the box.
 *
 * The sign is settled against the prototype's own layout engine, not inferred:
 * `scripts/verify-prototype-wrappers.mjs` agrees on 534 depths and 209
 * placements using `protoFrontZ = parentDepth/2 - zOffset - depth/2`. Rearrange
 * it and a piece's centre sits `-zOffset` INTO the screen from the container
 * centre; trace's +z points out of the screen, so trace z = `+zOffset`.
 *
 * The original calibration that motivated negating compared a prototype
 * WORLD-space number (+0.321, left-handed, behind centre) against a
 * centre-relative extractor value (-0.310, also behind centre): the same
 * position in two frames, mirrored numerals — evidence FOR the unnegated
 * reading, read as evidence for negation.
 *
 * `node.z` on templates keeps its own negation. That one genuinely is a
 * different vocabulary — a near-face depth running INTO the screen — and is
 * converted alongside x and y in the walk. See CONTRACT.md §5.1.
 */
function zOffsetMetres(props: TemplateNodeProps): number {
    return (props.zOffset ?? 0) * DU_TO_M;
}

/**
 * Euler degrees → quaternion [x, y, z, w], Babylon YXZ (yaw-pitch-roll)
 * convention — matches TransformNode.rotation semantics on load.
 */
function quaternionFromEulerDegrees(deg: { x?: number; y?: number; z?: number }): [number, number, number, number] {
    const rad = Math.PI / 180;
    const hx = (deg.x ?? 0) * rad / 2, hy = (deg.y ?? 0) * rad / 2, hz = (deg.z ?? 0) * rad / 2;
    const sx = Math.sin(hx), cx = Math.cos(hx);
    const sy = Math.sin(hy), cy = Math.cos(hy);
    const sz = Math.sin(hz), cz = Math.cos(hz);
    return [
        sx * cy * cz + cx * sy * sz,
        cx * sy * cz - sx * cy * sz,
        cx * cy * sz - sx * sy * cz,
        cx * cy * cz + sx * sy * sz,
    ];
}

/**
 * Bevel corner radius in METRES (#759), the unit the solver hands out.
 * Template radii are authored in du, so this is the same DU_TO_M scale the
 * sizes take — exact for every node, fixed or Hug/Fill, where the old
 * fraction had to guess 0.06 whenever the du size was absent.
 *
 * A du radius at or past half the smaller side is a capsule, not a big
 * fillet: `pill` says that at whatever size the solver lands on, which an
 * absolute radius cannot. Only decidable when the node has fixed du sizes;
 * a Hug/Fill node keeps the metre radius and the render-time clamp.
 */
function bevelShape(props: TemplateNodeProps): Pick<BevelData, 'cornerRadius' | 'pill'> {
    const du = props.radius ?? 8;
    const w = typeof props.size?.x === 'number' ? props.size.x : undefined;
    const h = typeof props.size?.y === 'number' ? props.size.y : undefined;
    const minSide = w !== undefined && h !== undefined ? Math.min(w, h) : (w ?? h);
    if (minSide && du / minSide >= 0.5) return { cornerRadius: minSide * 0.5 * DU_TO_M, pill: true };
    return { cornerRadius: du * DU_TO_M };
}

/**
 * Card body node for a container, if it should render one: the root always
 * gets the canvas backdrop; framed or explicitly colored containers get a
 * card body. Everything else stays an invisible flow container.
 *
 * The body is composition, not a bespoke behavior: a cube slab child,
 * absolutely positioned + Fill-sized (CSS position:absolute; inset:0),
 * corners rounded by Bevel, painted by Renderer. Clients without Bevel
 * render a sharp slab; the size is baked in the transform by the solve.
 */
function cardBodyData(
    props: TemplateNodeProps,
    isRoot: boolean,
    name: string,
    containerDepth: number,
): SceneObjectData | null {
    const explicit = resolveValue(props.color);
    const colored = typeof explicit === 'string' && explicit !== 'transparent';
    // An OPEN colour freedom is not "no colour". It is the template marking the
    // container as a SURFACE and leaving the fill to the design — the hints say
    // so outright ("The surface - one solid fill from the style"). Reading it as
    // absent is why the pill templates rendered no pills: pillslide-pill-columns
    // authors all four of its columns this way, scored 0/5 on structure against
    // the prototype, and its capsule shape lives in a radius those bodies carry.
    // 83 containers across 27 of the 47 templates are authored like this, which
    // made it the single largest silent drop in the library.
    const openSurface = isOpen(props.color);
    if (!isRoot && !props.frame && !colored && !openSurface) return null;
    const item: LayoutItemData = {
        '@type': 'LayoutItem',
        positionMode: 'Absolute',
        widthMode: 'Fill', heightMode: 'Fill',
        depthMode: 'Fixed', depth: 0.006,
    };
    const bevel: BevelData = { '@type': 'Bevel', ...bevelShape(props) };
    const renderer: RendererData = {
        '@type': 'Renderer',
        materials: [{
            targetMaterialIndex: 0,
            // A colour the TEMPLATE authored stays literal — that is the
            // template's own choice, not the theme's. Everything else binds,
            // so a later style swap reaches it.
            baseColorFactor: colored ? explicit : (isRoot ? THEME_BINDINGS.background : THEME_BINDINGS.surface),
        }],
    };
    return {
        name: `${name} Body`,
        src: CUBE_SRC,
        // Rest the slab against the container's BACK plane (Absolute items
        // keep authored positions). Centered at z 0 its front face (+3 mm)
        // sits exactly flush with front-aligned leaf content at the ladder
        // floor — grazing-angle z-fighting (striped hero images).
        transform: { translation: [0, 0, -containerDepth / 2] },
        behaviors: [item, bevel, renderer],
    };
}

/** Display text threshold: du fontSize at/above this takes the heading face. */
const HEADING_FONT_SIZE_DU = 40;

/**
 * What each palette role binds to in the published theme vocabulary.
 *
 * Generation emits these bindings rather than resolved colours, which is what
 * makes a post-generation style swap exact: the scene references the theme, so
 * replacing the theme replaces every surface at once. Baking the colours in —
 * as this did — severs that link and leaves swapping to re-walk the scene
 * guessing which literals came from the old style.
 */
export const THEME_BINDINGS = {
    background: '{backgroundColor}',
    surface: '{panelColor}',
    accent: '{accentColor}',
    text: '{backgroundTextColor}',
    titleFont: '{titleFont}',
    bodyFont: '{bodyFont}',
} as const;

/**
 * Text data as this converter writes it. `fontFamily` holds a theme binding
 * (`{titleFont}`), not a family name, so it is typed as the plain string it is;
 * trace-viewer types the field as its family names.
 */
type ThemedTextData = Omit<TextData, 'fontFamily'> & { fontFamily?: string };

function textBehavior(props: TemplateNodeProps, name: string): ThemedTextData {
    // Open (AI-fillable) text slots start EMPTY — rendering the slot name as
    // placeholder copy ghosts through whatever the model overlays ("title"
    // double-struck under the real headline). The hint travels in the tool
    // result, not the scene.
    let text = isOpen(props.content) ? '' : resolveValue(props.content) ?? name;
    if (props.textTransform === 'uppercase') text = text.toUpperCase();
    const data: ThemedTextData = { '@type': 'Text', text };
    if (props.fontSize !== undefined) data.fontSize = props.fontSize / 10;
    if (props.fontWeight !== undefined) data.fontWeight = props.fontWeight >= 600 ? 'bold' : 'regular';
    if (props.letterSpacing !== undefined) data.letterSpacing = props.letterSpacing;
    if (props.lineHeight !== undefined) data.lineHeight = props.lineHeight;
    const color = resolveValue(props.color);
    // Template-authored colour stays literal; otherwise bind, so a style swap
    // reaches the text too.
    data.color = typeof color === 'string' && color !== 'transparent' ? color : THEME_BINDINGS.text;
    const isDisplay = (props.fontSize ?? 0) >= HEADING_FONT_SIZE_DU;
    data.fontFamily = isDisplay ? THEME_BINDINGS.titleFont : THEME_BINDINGS.bodyFont;
    return data;
}

/**
 * Fold a node's own `hint` into the slot's. Both are author guidance for the
 * same hole; the slot-kind hint says what to put there, the node hint qualifies
 * it (CONTRACT.md §3.5). A node hint with no slot becomes template guidance.
 */
function withNodeHint(hint: string | undefined, props: TemplateNodeProps): string | undefined {
    if (!props.hint) return hint;
    return hint ? `${hint} ${props.hint}` : props.hint;
}

/**
 * Template-supplied artwork, under either spelling: `properties.src` is the
 * declared field, `semanticProps.src` is what the editor writes on save — a
 * check for one alone misclassifies the half of the library that roundtripped
 * through the editor. A node that already carries artwork is decor, not a
 * hole: offering it as a slot inflates the router's capacity count (hero-card
 * read as six 3D subjects, and a sports-car request rendered spec text over a
 * 70% empty card) and invites the fill to overwrite the artwork that makes
 * the template look like itself. A blank src (empty or whitespace) counts as
 * NO artwork — clearing a src is how a template says "fill me".
 */
function hasAuthoredArtwork(node: TemplateNode, props: TemplateNodeProps): boolean {
    const semantic = (node as { semanticProps?: { src?: unknown } }).semanticProps?.src;
    return (typeof props.src === 'string' && props.src.trim() !== '')
        || (typeof semantic === 'string' && semantic.trim() !== '');
}

/** Slot classification for one node, if it is AI-fillable. */
function slotFor(node: TemplateNode, path: string, nodeIndex: number): TemplateSlot | null {
    const props = node.properties ?? {};
    const optional = props.optional === true;
    if (node.type === 'text') {
        if (!isOpen(props.content)) return null;
        let hint = openHint(props.content);
        const colorHint = openHint(props.color);
        if (colorHint) hint = `${hint ?? ''} Color: ${colorHint}`.trim();
        return { path, nodeIndex, kind: 'text', hint: withNodeHint(hint, props), optional };
    }
    if (node.type === 'image') {
        if (hasAuthoredArtwork(node, props)) return null;
        return {
            path, nodeIndex, kind: 'image',
            hint: withNodeHint(openHint(node.imageIntent) ?? props.fill?.hint, props), optional,
        };
    }
    if (node.type === 'model3d') {
        // Template-supplied geometry, not a hole: a fixed `src` means the
        // artifact already decided what stands here (CONTRACT.md §3.7).
        if (hasAuthoredArtwork(node, props)) return null;
        const slot: TemplateSlot = {
            path, nodeIndex, kind: 'model',
            hint: withNodeHint(openHint(node.modelIntent) ?? props.fill?.hint, props), optional,
        };
        const anchor = modelAnchor(node);
        if (anchor) slot.anchor = anchor;
        return slot;
    }
    if (props.fill?.kind === 'ai-fill') {
        return { path, nodeIndex, kind: 'content', hint: withNodeHint(props.fill.hint, props), optional };
    }
    return null;
}

/**
 * Statically resolve 'fill' X-extents where the chain is decidable from the
 * artifact alone (fixed root width → fill rows → fill columns). The runtime
 * engine resolves fill against a definite parent, but fill-inside-hug/-fill
 * chains need its (not yet built) multi-pass stabilization — until then a
 * fill column falls back to the 1m default and rows explode. Mutates a deep
 * copy of the tree, turning decidable fills into numbers.
 */
function resolveStaticWidths(root: TemplateNode): TemplateNode {
    const clone = structuredClone(root);
    const walk = (node: TemplateNode, knownW: number | undefined): void => {
        const props = node.properties ?? {};
        const own = props.size?.x;
        // A hug row with fill children is width-promoted at conversion time
        // (see layoutBehavior) — mirror that here so its extent is decidable.
        const promoted = (own === 'hug' || own === undefined)
            && props.direction === 'horizontal'
            && (node.children ?? []).some(c => c.properties?.size?.x === 'fill');
        const w = typeof own === 'number' ? own : (own === 'fill' || promoted) ? knownW : undefined;
        if (own === 'fill' && w !== undefined) props.size!.x = w;

        const children = node.children ?? [];
        if (!children.length) return;
        const gap = props.gap ?? 0;
        const pad = typeof props.padding === 'number'
            ? props.padding * 2
            : (props.padding?.left ?? 0) + (props.padding?.right ?? 0);
        const inner = w !== undefined ? w - pad : undefined;

        if (props.direction === 'horizontal' && inner !== undefined) {
            // Distribute the row: fixed children keep their width, fill children
            // split the remainder (flexGrow-weighted).
            const fills = children.filter(c => c.properties?.size?.x === 'fill');
            const fixed = children.reduce((s, c) => {
                const cx = c.properties?.size?.x;
                return s + (typeof cx === 'number' ? cx : 0);
            }, 0);
            const free = inner - fixed - gap * (children.length - 1);
            const totalGrow = fills.reduce((s, c) => s + (c.properties?.flexGrow ?? 1), 0) || 1;
            for (const c of fills) {
                const share = free * ((c.properties?.flexGrow ?? 1) / totalGrow);
                if (share > 0) c.properties!.size!.x = Math.round(share);
            }
            for (const c of children) walk(c, typeof c.properties?.size?.x === 'number' ? c.properties.size.x : undefined);
        } else {
            // Column/depth stacks: children span the inner width when fill.
            for (const c of children) walk(c, inner);
        }
    };
    walk(clone, typeof clone.properties?.size?.x === 'number' ? clone.properties.size.x : undefined);
    return clone;
}

export interface ConvertOptions {
    /** Design-set name substituted into asset-node `{set}` placeholders. */
    set?: string;
    /**
     * Whether the root container renders a canvas body (default true).
     * Wrapper trees pass false — staging is invisible scaffolding plus its
     * design-set GLBs, never a painted slab.
     */
    rootCanvas?: boolean;
    /**
     * Skip doodad-type components (default true for staging — accent meshes
     * read as clutter without the prototype's curation/animation pass).
     */
    omitDoodads?: boolean;
    /** Per-GLB-filename size metrics for the shrink clamp (wrapper assets). */
    componentMetrics?: Record<string, ComponentSizeMetrics>;
}

export function convertTemplate(artifact: TemplateArtifact, options?: ConvertOptions): ConvertedTemplate {
    const nodes: ConvertedNode[] = [];
    const slots: TemplateSlot[] = [];
    const guidance: string[] = [];
    const designSet = options?.set ?? 'Core';
    const rootCanvas = options?.rootCanvas ?? true;
    const omitDoodads = options?.omitDoodads ?? true;

    // Suppressing doodads removes clutter from around content; it must not
    // delete the wrapper itself. A few wrappers (display-laurel, display-ring)
    // are ENTIRELY doodads — their identity is the arrangement — so omitting
    // them left an empty mount that rendered identically to every other
    // emptied wrapper. Three catalog entries were silently the same stage, and
    // only the render harness's identical-capture guard noticed. Where dropping
    // doodads would leave nothing to see, keep them.
    const dropDoodads = omitDoodads && hasNonDoodadGeometry(artifact.tree);

    // Sibling names must be unique or the scene cannot be exported — the .tracex
    // writer rejects duplicates outright rather than silently losing one. The
    // wrapper library trips this constantly and by design: a fin wall is six
    // sibling nodes all authored as "Background", a colonnade twelve "Base".
    // 49 of the 60 wrappers carry at least one such group, so every scene staged
    // with them exported as a hard failure until this suffixed them apart.
    const siblingNames = new Map<number, Set<string>>();
    const uniqueSiblingName = (parentIndex: number, base: string): string => {
        let taken = siblingNames.get(parentIndex);
        if (!taken) { taken = new Set(); siblingNames.set(parentIndex, taken); }
        if (!taken.has(base)) { taken.add(base); return base; }
        let n = 2;
        while (taken.has(`${base} ${n}`)) n++;
        const name = `${base} ${n}`;
        taken.add(name);
        return name;
    };

    const walk = (
        node: TemplateNode, parentIndex: number, parentPath: string,
        parent?: TemplateNode, level = 0,
    ): void => {
        const isRoot = parentIndex < 0;
        // A page's authored background colour becomes an ordinary surface
        // colour before anything else reads it. Catalog components need no such
        // step any more: the port script resolves the filename, so the artifact
        // already says `type: "asset"` (CONTRACT.md §5.2).
        node = surfaceColorNode(node) ?? node;
        const name = isRoot ? artifact.name : uniqueSiblingName(parentIndex, displayName(node));
        const path = isRoot ? '' : (parentPath ? `${parentPath}/${name}` : name);
        const props = node.properties ?? {};

        // An out-of-flow node cannot be filled BY the flow, so its `fill` has to
        // be resolved against the parent's authored box here or it resolves to
        // nothing at all. The booth's backdrop is authored `fill × fill × 600`
        // and absolute: its width came out right only because a separate pass
        // statically resolves fill WIDTHS, while the height fell through as
        // undefined — MorphStretch got no height, the mesh stayed at its 1.0 m
        // rest, and the backdrop floated half-height above the base instead of
        // standing on it (proto spans 0..2.001; we spanned 0.500..1.501).
        const outOfFlow = node.position === 'absolute' || props.absolute;
        const fillFrom = (
            v: number | 'hug' | 'fill',
            parentValue: number | 'hug' | 'fill' | undefined,
        ): number | 'hug' | 'fill' =>
            (v === 'fill' && outOfFlow && typeof parentValue === 'number') ? parentValue : v;
        const sized: TemplateNodeProps = props.size
            ? {
                ...props,
                size: {
                    x: fillFrom(props.size.x, parent?.properties?.size?.x),
                    y: fillFrom(props.size.y, parent?.properties?.size?.y),
                    z: fillFrom(props.size.z, parent?.properties?.size?.z),
                },
            }
            : props;

        let behaviors: SceneObjectData['behaviors'];
        let bodyData: SceneObjectData | null = null;
        let assetSrc: string | undefined;
        if (node.type === 'asset' && props.asset) {
            if (props.componentType === 'doodad' && dropDoodads) return;
            // Design-set GLB (wrapper set piece): the layout positions it;
            // MorphStretch sizes it to its box by driving the asset's
            // authored stretch morphs, so borders and corners keep their
            // sculpted proportions instead of leaf-scale smearing.
            assetSrc = componentAssetPath(props.asset, designSet);
            const requested = {
                width: typeof sized.size?.x === 'number' ? sized.size.x * DU_TO_M : undefined,
                height: typeof sized.size?.y === 'number' ? sized.size.y * DU_TO_M : undefined,
                depth: typeof sized.size?.z === 'number' ? sized.size.z * DU_TO_M : undefined,
            };
            const clamped = clampComponentSize(requested, options?.componentMetrics?.[assetSrc.split('/').pop() ?? '']);
            const item = { '@type': 'LayoutItem', ...itemFields(sized, node) } as LayoutItemData;
            if (clamped.width !== undefined) { item.width = clamped.width; item.widthMode = 'Fixed'; }
            if (clamped.height !== undefined) { item.height = clamped.height; item.heightMode = 'Fixed'; }
            if (clamped.depth !== undefined) { item.depth = clamped.depth; item.depthMode = 'Fixed'; }
            behaviors = [item];
            const stretch: MorphStretchData = { '@type': 'MorphStretch', ...clamped };
            if (stretch.width !== undefined || stretch.height !== undefined || stretch.depth !== undefined) {
                behaviors.push(stretch);
            }
        } else if (node.type === 'image' && typeof props.src === 'string' && props.src) {
            // Image with a FIXED src (parity artifacts share identical media
            // between engines) — materializes as a real Image object instead
            // of an open slot.
            assetSrc = props.src;
            behaviors = [{ '@type': 'LayoutItem', ...itemFields(props, node) }, { '@type': 'Image' }];
        } else if (node.type === 'model3d' && typeof props.src === 'string' && props.src) {
            // Model with a FIXED src is template-SUPPLIED geometry, not a hole
            // for the design to fill (CONTRACT.md §3.7). It fell through to the
            // container branch, which is worse than the recorded symptom: the
            // node came out as an empty Layout with no `src` at all, so the
            // iPhone body was never loaded — and was simultaneously offered to
            // the model as a slot to fill. Materialize it like the fixed-src
            // image: real object, real src, no slot.
            //
            // It does NOT stretch to its authored box (an earlier note here
            // said so): `src` brings a `Model`, `Model` is a LayoutSizeProvider
            // with `preservesNodeScale`, and the solver leaf-scales only plain
            // leaves — so the GLB renders at its NATURAL size, positioned in a
            // slot sized from the artifact. That is `object-fit: none` where
            // the artifact asked for `contain` (tv#738, CONTRACT.md §4.1).
            assetSrc = props.src;
            // No `Model` entry: `src` already implies it. SceneCreation's
            // "src as template" rule adds the primitive behavior for a model
            // extension, and the exporter drops a redundant one — emitting it
            // is data that never survives a round trip.
            behaviors = [{ '@type': 'LayoutItem', ...itemFields(sized, node) }];
        } else if (node.type === 'text') {
            const text = textBehavior(props, name);
            behaviors = [{ '@type': 'LayoutItem', ...itemFields(props, node) }, text];
        } else {
            // Layout reads `props` to retain Fill height for live resizing;
            // resolved `sized` dimensions are used for absolute placement.
            const layout = layoutBehavior(props, node);
            // A flat artifact depth is a 2D convention, not intent — and so is
            // everything else the artifact says about the depth axis.
            const authoredDepth = layout.depth;
            const flatDepth = authoredDepth === undefined || authoredDepth < FLAT_DEPTH_THRESHOLD;
            const containerDepth = flatDepth ? ladderDepth(level) : authoredDepth;
            if (flatDepth) {
                layout.depth = containerDepth;
                layout.depthMode = 'Fixed';
            }
            // Front-align the depth axis. 'Center' would collapse every nesting
            // level back onto z = 0 and 'Start' is worse: back-aligning makes a
            // child's back plane coincide with its parent's BY CONSTRUCTION
            // (childBack = parentBack, whatever the depths), so every container
            // that rests a body on its own back plane lands that body exactly
            // on its ancestor's — a depth-buffer tie that renders as stippled
            // moiré. 196 containers in the library author "Start" beside
            // `z: 1 du`; it is the same DOM-canvas default as the flat depth
            // itself, and reading it as depth intent is what buried them.
            //
            // A container with a REAL authored depth is a different animal —
            // wrapper set pieces and depth stacks (depth-deck, the exhibit
            // table's model slots) mean their cross-B and are honored.
            layout.alignCrossB = (node.type === 'model3d' ? MODEL_ANCHOR_CROSS_B[modelAnchor(node) ?? ''] : undefined)
                ?? (flatDepth ? 'End' : props.alignCrossB ?? 'End');
            behaviors = [layout];
            bodyData = cardBodyData(props, isRoot && rootCanvas, name, containerDepth);
        }

        // Template-authored behaviors ride along after the ones the conversion
        // builds: the layout/item data is what positions the node, and the
        // authored behavior acts on the result (CONTRACT.md §3.2).
        behaviors = [...behaviors ?? [], ...templateBehaviors(props.behaviors)];

        const index = nodes.length;
        const data: SceneObjectData = { name, behaviors };
        if (assetSrc) data.src = assetSrc;
        if (props.rotation) {
            data.transform = { rotation: quaternionFromEulerDegrees(props.rotation) };
        }

        // Absolute nodes keep their authored offset: artifact coords are the
        // node's top-left within the parent, du, Y-down; the scene wants the
        // node's centre in parent-local metres, Y-up. Hug extents are unknown
        // pre-solve — treat them as 0 (anchor the authored point). Z lifts to
        // the parent's ladder depth: overlays/breakouts must sit in front of
        // the parent's flowed content (which front-flushes below that plane) —
        // at z 0 they z-fight the very content they overlay.
        //
        // Templates express the authored point as node.x/node.y; the wrapper
        // extractor writes the same thing as properties.offset. Two names for
        // one concept, and only the first was read — so every wrapper's
        // absolutely-placed set piece landed at the parent's centre. The booth
        // is the clearest case: its two side walls are authored at x -460 and
        // 2480 inside a 3000-wide mount, which is symmetric about the centre,
        // and both rendered stacked in the middle instead of enclosing
        // anything. Confirmed against the prototype's own resolved positions,
        // which come back as exactly those authored values.
        if (!isRoot && (node.position === 'absolute' || props.absolute)) {
            const dim = (v: number | 'hug' | 'fill' | undefined): number => typeof v === 'number' ? v : 0;
            const pw = dim(parent?.properties?.size?.x);
            const ph = dim(parent?.properties?.size?.y);
            // An out-of-flow Fill axis uses its numeric parent extent
            // when converting the authored top-left point to a centre.
            const w = dim(sized.size?.x);
            const h = dim(sized.size?.y);
            const ax = node.x ?? props.offset?.x ?? 0;
            const ay = node.y ?? props.offset?.y ?? 0;
            const pd = dim(parent?.properties?.size?.z);
            const d = dim(sized.size?.z);
            data.transform = {
                ...data.transform,
                translation: [
                    (ax + w / 2 - pw / 2) * DU_TO_M,
                    (ph / 2 - ay - h / 2) * DU_TO_M,
                    // `offset.z` is a top-left-style authored point exactly as
                    // x and y are, so it takes the same conversion. Applying it
                    // RAW put the booth's side walls at z 0.460 where the
                    // prototype resolves them to 0.000; the shared formula
                    // gives 460 + 30 - 490 = 0, which is the prototype's number
                    // to the millimetre.
                    //
                    // Like node.z, offset.z runs into the screen in the
                    // prototype, so its centre conversion is negated here.
                    //
                    // `zOffset` is a different thing — a depth-stack layer the
                    // extractor bakes, already relative to the container centre,
                    // positive toward the front face — and it is carried through
                    // UNNEGATED. See zOffsetMetres: the vitrine calibration that
                    // once justified negating here (prototype +0.321 against
                    // extractor -0.310) compared a world-space number with a
                    // centre-relative one, which is the same position in two
                    // frames rather than two conventions.
                    //
                    // `node.z` is the TEMPLATE vocabulary's authored depth, the
                    // exact counterpart of node.x/node.y: a near-face point in
                    // du within the parent's depth box. It IS negated — unlike
                    // zOffset — because the artifact's z runs into the screen
                    // and trace's runs out of it. Three templates
                    // pin the sign independently, and all three only come out
                    // physically right this way: the semicircle's placard is
                    // hinted "standing in front of the bench" and is authored
                    // BELOW the bench's z (-220 vs 0); the exhibit table's
                    // caption rail sits at 90 against back panels at 990; the
                    // same table's arc of tiles stands behind its centrepiece.
                    // Read unnegated, every one of them turns inside out.
                    //
                    // Without this the depth axis was dropped outright and
                    // depth-deck — five cards whose ONLY differentiation is a
                    // 950→430 z cascade — collapsed onto the ladder plane.
                    node.z !== undefined ? -(node.z + d / 2 - pd / 2) * DU_TO_M
                        : props.offset?.z !== undefined ? -(props.offset.z + d / 2 - pd / 2) * DU_TO_M
                            : props.zOffset !== undefined ? zOffsetMetres(props)
                                : ladderDepth(level - 1),
                ],
            };
        }

        nodes.push({ parentIndex, data });
        if (bodyData) {
            // The body is a child of this node, so it shares the sibling
            // namespace with this node's own children — reserve it before they
            // are walked.
            bodyData.name = uniqueSiblingName(index, bodyData.name);
            nodes.push({ parentIndex: index, data: bodyData });
        }

        const slot = slotFor(node, path, index);
        if (slot) slots.push(slot);
        else if (props.hint) guidance.push(props.hint);

        for (const child of node.children ?? []) {
            walk(child, index, path, { ...node, properties: sized }, level + 1);
        }
    };

    walk(resolveStaticWidths(artifact.tree), -1, '');
    return { nodes, slots, guidance };
}
